import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { formatRelativeTime } from "@/lib/format";
import { useLocation } from "wouter";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Ticket as TicketIcon } from "lucide-react";
import type { Ticket } from "@db/schema";

const columns: ColumnDef<Ticket>[] = [
  {
    key: "key",
    header: "Key",
    cell: (row) => (
      <span className="font-mono text-sm">{row.key ?? "\u2014"}</span>
    ),
  },
  {
    key: "subject",
    header: "Subject",
    cell: (row) => (
      <span className="truncate max-w-[250px] block">
        {row.subject ?? "No subject"}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => <StatusBadge type="ticket" status={row.status} />,
  },
  {
    key: "priority",
    header: "Priority",
    cell: (row) => <StatusBadge type="priority" status={row.priority} />,
  },
  {
    key: "updatedAt",
    header: "Last Update",
    cell: (row) => (
      <span className="text-sm text-muted-foreground">
        {formatRelativeTime(row.updatedAt)}
      </span>
    ),
  },
];

export default function ClientTicketsPage() {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const queryParams = new URLSearchParams();
  if (search) queryParams.set("search", search);
  if (status && status !== "all") queryParams.set("status", status);
  const queryString = queryParams.toString();
  const url = `/api/portal/tickets${queryString ? `?${queryString}` : ""}`;

  const { data: tickets, isLoading } = useQuery<Ticket[]>({
    queryKey: [url],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Tickets"
        description="View and manage your support tickets"
      />

      <DataTable
        columns={columns}
        data={tickets}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/portal/tickets/${row.id}`)}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search tickets..."
        filterSlot={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        }
        emptyIcon={TicketIcon}
        emptyTitle="No tickets yet"
        emptyDescription="You don't have any support tickets. Submit a request to get started."
      />
    </div>
  );
}
